import { firestoreAction } from 'vuexfire';

import { db, FieldPath } from '../../firebase';
import { isNullOrUndef } from '../../utils/misc';
import { startsWithQuery } from '../../firebase/utils';

/**
 *
 * @param {Query|CollectionReference} query - query to apply the operator on.
 * @param {String} filterFieldName - FiledName to apply the filter for.
 * @param {String} operator - operator that has to be applied.
 * @param {String} currentValue - value (if any), to apply with the operator.
 *
 * @return {Query} - query after applying the filter.
 */
function applyFilter(query, filterFieldName, operator, currentValue) {
  switch (operator) {
    case '==':
    case '>':
    case '<':
    case '>=':
    case '<=':
    case 'array-contains':
    case 'in':
    case 'array-contains-any':
      return currentValue.length
        ? query.where(filterFieldName, operator, currentValue)
        : query;
    case 'reference':
      return query.where(filterFieldName, '==', db.doc(currentValue));
    case 'orderBy':
      return query.orderBy(filterFieldName, currentValue || 'asc');
    case 'starts-with':
      return isNullOrUndef(currentValue)
        ? query
        : startsWithQuery(
            query,
            filterFieldName == 'id' ? FieldPath.documentId() : filterFieldName,
            currentValue
          );
    default:
      return query;
  }
}

/**
 * This method is used by the plugin to apply the filter.
 *
 * @param {Query|CollectionReference} ref - Firestore Query / CollectionReference
 * @param {Object} filterDefinition - an Map defining the types of filters that can be applied.
 * The paginate plugin takes an argument named filterDefinition.
 * which is directly passed here. this object defines the kinds of filters that
 * can be applied to current module.
 *
 * For example:
 * The following configuration would mean that, the module will allow to
 * run an array-contains filter on field name `tags`.
 * `
 * filterDefinition: {
 *   tags: 'array-contains'
 * }
 * `
 * @param {Object} currentFilters - This is used to apply those filters.
 * This object can either be updated from the an action name setFilter or
 * can be defined with a default value during the setup of the plugin.
 * using the plugin argument filterDefault.
 *
 * @returns {Query} - after applying all the currentFilters.
 */
function getFilteredQuery(ref, filterDefinition, currentFilters) {
  let query = ref;
  Object.keys(filterDefinition).forEach(filterFieldName => {
    if (Array.isArray(filterDefinition[filterFieldName])) {
      filterDefinition[filterFieldName].forEach((operator, index) => {
        if (
          Array.isArray(currentFilters[filterFieldName]) &&
          !isNullOrUndef(currentFilters[filterFieldName][index])
        ) {
          query = applyFilter(
            query,
            filterFieldName,
            operator,
            currentFilters[filterFieldName][index]
          );
        }
      });
    } else {
      if (!isNullOrUndef(currentFilters[filterFieldName])) {
        query = applyFilter(
          query,
          filterFieldName,
          filterDefinition[filterFieldName],
          currentFilters[filterFieldName]
        );
      }
    }
  });
  return query;
}

/**
 * Use when collection/query is required to be paginated.
 *
 * Make sure that the `module` has been added to mutationTypes before calling
 * `paginate` on the module.
 * @param {String} collectionPath - collection path from which documents have to be fetched
 * should be paginated or key in the state from where to take such ref
 * @param {Number} [options] __last three params are expected in this single object making them optional__
 * @param {Number} [options.pageSize=10] - number of documents per page
 * @param {Object} [options.filterDefinition={}] - an Map defining the types of filters to apply
 * @param {Object} [options.filterDefault={}] - default filter that has to be applied.
 *
 * @returns {Function} that will take a module and apply the plugin.
 */
const paginate = (collectionPath, options) => {
  return function(module) {
    const { pageSize = 10, filterDefinition, filterDefault } = options || {};
    const ref = db.collection(collectionPath);
    const filterEnabled = filterDefinition !== undefined;

    const ucName = module.moduleName.toUpperCase();
    const GET_INIT = `${ucName}_GET_INIT`;
    const GET_DONE = `${ucName}_GET_ERROR`;
    const GET_ERROR = `${ucName}_GET_DONE`;
    const SET_FILTER = `${ucName}_SET_FILTER_INIT`;

    module.addMutationTypes([GET_INIT, GET_DONE, GET_ERROR, SET_FILTER]);

    const state = {
      list: [],
      currentPage: 1,
      loadingPage: false,
      pageError: null,
      pageStarts: [],
      filters: { ...filterDefault } || {}
    };

    const getters = {
      currentPageSize: function(state) {
        return state.list.length;
      },
      hasNext: function(state) {
        return state.list.length === pageSize;
      },
      hasPrev: function(state) {
        return state.currentPage > 1;
      },
      nextPageStart: function(state) {
        const { list } = state;
        if (list.length > 0) {
          return ref.doc(list[list.length - 1].id);
        }
        return null;
      },
      prevPageStart: function(state) {
        const { currentPage, pageStarts } = state;
        if (pageStarts[currentPage - 2]) {
          return ref.doc(pageStarts[currentPage - 2]);
        }
        return null;
      },
      getAvailableFilters() {
        return { ...filterDefinition };
      }
    };

    const mutations = {
      [GET_INIT](state) {
        state.loadingPage = true;
        state.pageError = null;
      },
      [GET_DONE](state, page) {
        state.loadingPage = false;
        state.currentPage = page;
        const firstDoc = state.list[0];
        if (firstDoc) {
          if (state.pageStarts.length >= page) {
            state.pageStarts[page - 1] = firstDoc.id;
          } else {
            state.pageStarts.push(firstDoc.id);
          }
        }
      },
      [GET_ERROR](state, data) {
        state.loadingPage = false;
        state.pageError = data;
      },
      [SET_FILTER](state, filters) {
        state.filters = { ...state.filters, ...filters };
      }
    };

    const actions = {
      bindRef: firestoreAction(async ({ bindFirestoreRef }, ref) => {
        await bindFirestoreRef('list', ref, { maxRefDepth: 1 });
      }),
      async get({ commit, dispatch, getters, state }, type) {
        try {
          commit(module.con.LOADING_INIT, null, { root: true });
          commit(GET_INIT);
          let query, nextPage;
          query = filterEnabled
            ? getFilteredQuery(ref, filterDefinition, state.filters)
            : ref;
          if (type === 'next' && getters.nextPageStart) {
            const refDoc = await getters.nextPageStart.get();
            query = query.startAfter(refDoc).limit(pageSize);
            nextPage = state.currentPage + 1;
          } else if (type === 'prev' && getters.prevPageStart) {
            const refDoc = await getters.prevPageStart.get();
            nextPage = state.currentPage - 1;
            query = query.startAt(refDoc).limit(pageSize);
          } else {
            query = query.limit(pageSize);
            nextPage = 1;
          }
          await dispatch('bindRef', query);
          commit(GET_DONE, nextPage);
        } catch (error) {
          commit(GET_ERROR, error);
        }
        commit(module.con.LOADING_DONE, null, { root: true });
      },
      async setFilter({ commit, dispatch }, filters) {
        commit(SET_FILTER, filters);
        await dispatch('get');
      }
    };

    return module.extend({ state, getters, mutations, actions });
  };
};

export default paginate;
